/**
 * TraderSystem: everything about the wandering trader and the llamas that needs
 * the whole world rather than one mob (the glue main.ts builds, like VillageSystem):
 *  - the daily spawn roll (trades.ts TraderSpawnRoll) and placing a trader with
 *    two leashed trader llamas, never more than one trader alive;
 *  - right-clicks on traders (trade screen) and llamas (feed, tame, chest);
 *  - caravans (llamas a player leads pull loose llamas into a line);
 *  - ticking the llama spit projectiles;
 *  - keeping traders that are too far to simulate counting down to despawn,
 *    re-tying trader llamas to their trader after a load, saving the roll state.
 * DOM-free: the trade and llama screens come in through `openTrade` / `openLlama`.
 */

import { collisionOf, IS_LAVA, IS_WATERY } from '../../blocks';
import { WORLD_HEIGHT } from '../../constants';
import { isSpawnEgg } from '../../game/spawnEggs';
import { isEmpty, type Stack } from '../../game/types';
import { ChunkState } from '../../world/chunk';
import type { EntityHost } from '../host';
import { leashes } from '../leash';
import type { EntityManager } from '../manager';
import type { Mob } from '../mob';
import { traderHooks, vanishedTraders } from './hooks';
import { CARAVAN_MAX, CARAVAN_RANGE, Llama } from './llama';
import { spits, type SpitEnv } from './spit';
import { WanderingTrader } from './trader';
import { TraderSpawnRoll } from './trades';

export interface TraderDeps {
  entities: EntityManager;
  host: EntityHost;
  dimension(): string;
  creative(): boolean;
  consumeHeld(n: number): void;
  openTrade(t: WanderingTrader, onClose: () => void): void;
  openLlama(l: Llama, onClose: () => void): void;
  sound?(id: string, x: number, y: number, z: number): void;
  /** Damage the local player (default: host.player.damage with a projectile source). */
  hurtPlayer?(amount: number, x: number, y: number, z: number, attackerId: number): void;
  /** Spawn-roll persistence: `[timer, chance]`. */
  state?: { load(): unknown; save(v: [number, number]): void };
}

/** Within this many blocks of the player a trader is simulated; beyond, TraderSystem counts its despawn down. */
const SIM_DIST = 96;

export class TraderSystem {
  readonly roll = new TraderSpawnRoll();
  private t = 0;
  private lastSaved = '';

  constructor(private readonly d: TraderDeps) {
    this.bind();
    vanishedTraders.clear();
    spits.clear();
    if (d.state) this.roll.load(d.state.load());
  }

  /** Point the module-level trader hooks at this system (a server re-binds per dimension). */
  bind(): void {
    traderHooks.mobs = () => this.d.entities.mobs;
    traderHooks.sound = this.d.sound ?? null;
  }

  /** A trader is alive somewhere (frozen far away counts). */
  hasTrader(): boolean { return this.d.entities.mobs.some((m) => m.type === 'wandering_trader' && m.alive && !m.removed); }

  // ------------------------------------------------------------------ tick

  tick(): void {
    this.t++;
    const d = this.d;
    if (d.dimension() === 'overworld' && this.roll.tick(d.host.rnd)) this.roll.result(this.trySpawn());
    if (this.t % 10 === 0) this.caravans();
    if (this.t % 20 === 0) this.maintain();
    spits.tick(this.spitEnv());
    if (this.t % 600 === 0) this.persist();
  }

  private persist(): void {
    if (!this.d.state) return;
    const s = this.roll.save();
    const key = `${s[0] - (s[0] % 600)}:${s[1]}`;
    if (key === this.lastSaved) return;
    this.lastSaved = key;
    this.d.state.save(s);
  }

  /** Frozen traders keep counting down; trader llamas re-tie to their trader once after a load. */
  private maintain(): void {
    const p = this.d.host.player;
    for (const m of this.d.entities.mobs) {
      if (m instanceof WanderingTrader) {
        if ((m.x - p.x) ** 2 + (m.z - p.z) ** 2 > SIM_DIST * SIM_DIST) for (let i = 0; i < 20; i++) m.tickFar();
      } else if (m instanceof Llama && m.needsLeash) {
        m.needsLeash = false;
        const tr = this.d.entities.mobs.find((x) => x.type === 'wandering_trader' && x.alive && (x as WanderingTrader).uid === m.traderUid);
        if (tr && !leashes.holderOf(m)) leashes.attach(m, { kind: 'mob', id: tr.id });
      }
    }
  }

  private spitEnv(): SpitEnv {
    const d = this.d, host = d.host;
    return {
      world: host.world,
      mobs: d.entities.mobs,
      player: host.player,
      hurtPlayer: (a, x, y, z, id) => {
        if (d.hurtPlayer) { d.hurtPlayer(a, x, y, z, id); return; }
        host.player.damage(a, { kind: 'projectile', x, y, z, attackerId: id });
      },
      hurtMob: (owner, target, amount) => this.hurtMob(owner, target, amount),
      ignores: (owner, m) => !!owner && (m.type === 'llama' || m.type === 'trader_llama') && (owner.type === 'llama' || owner.type === 'trader_llama'),
    };
  }

  /** Mob-vs-mob spit damage, with knockback, the hurt flash and the victim's loot when it dies (no XP: the player did not kill it). */
  private hurtMob(owner: Mob | null, target: Mob, amount: number): void {
    const h = this.d.host;
    if (!target.alive || !target.hurt(amount)) return;
    if (owner) target.knockback(target.x - owner.x, target.z - owner.z, 0.3, h.rnd);
    h.effect('hurt', target.x, target.y + target.height / 2, target.z);
    (target as unknown as { onHurtByMob?: (m: Mob) => void }).onHurtByMob?.(owner as Mob);
    if (owner) target.onHurtBy(owner.x, owner.z, h.rnd);
    if (target.dying) {
      h.effect('death', target.x, target.y + target.height / 2, target.z);
      for (const s of target.loot(h.rnd, false)) h.dropItem(s, target.x, target.y + target.height / 2, target.z);
    }
  }

  // --------------------------------------------------------------- caravans

  /** Llamas a player leads form up with the loose llamas around them. */
  private caravans(): void {
    const llamas = this.d.entities.mobs.filter((m): m is Llama => m instanceof Llama && m.alive);
    if (llamas.length === 0) return;
    const was = new Set(llamas.filter((l) => l.caravanPrev !== null));
    for (const l of llamas) l.caravanPrev = null;
    const used = new Set<Llama>();
    for (const head of llamas) {
      if (leashes.holderOf(head)?.kind !== 'player') continue;
      used.add(head);
      const cand = llamas
        .filter((l) => !used.has(l) && !l.baby && !leashes.isLeashed(l))
        .map((l) => ({ l, d: (l.x - head.x) ** 2 + (l.z - head.z) ** 2 }))
        .filter((c) => c.d <= (was.has(c.l) ? 24 : CARAVAN_RANGE) ** 2)
        .sort((a, b) => a.d - b.d)
        .slice(0, CARAVAN_MAX);
      let prev: Llama = head;
      for (const c of cand) { c.l.caravanPrev = prev; used.add(c.l); prev = c.l; }
    }
  }

  // ---------------------------------------------------------------- spawning

  /** Place a trader and two leashed trader llamas around (x, y, z); returns the trader. */
  spawnTrader(x: number, y: number, z: number, anchor: [number, number] | null = null): WanderingTrader | null {
    const em = this.d.entities;
    const t = em.spawnMob('wandering_trader', x, y, z);
    if (!(t instanceof WanderingTrader)) { if (t) t.removed = true; return null; }
    t.anchor = anchor ?? [x, z];
    for (let i = 0; i < 2; i++) {
      const spot = this.floorNear(x, z, 4) ?? [x, y, z];
      const l = em.spawnMob('trader_llama', spot[0], spot[1], spot[2]);
      if (!(l instanceof Llama)) continue;
      l.traderUid = t.uid;
      leashes.attach(l, { kind: 'mob', id: t.id });
      em.refreshView(l);
    }
    return t;
  }

  private chunkReady(x: number, z: number): boolean {
    return this.d.host.world.getChunk(x >> 4, z >> 4)?.state === ChunkState.Ready;
  }

  private free(x: number, y: number, z: number): boolean {
    const id = this.d.host.world.getBlock(x, y, z);
    return collisionOf(id).length === 0 && IS_WATERY[id] === 0 && IS_LAVA[id] === 0;
  }

  /** Feet cell [x, y, z] (block coords) on dry ground in the column, or null. */
  private surface(x: number, z: number): [number, number, number] | null {
    if (!this.chunkReady(x, z)) return null;
    const w = this.d.host.world;
    let y = WORLD_HEIGHT - 3;
    while (y > 1 && collisionOf(w.getBlock(x, y, z)).length === 0 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
    if (y <= 1 || IS_WATERY[w.getBlock(x, y, z)] === 1) return null;
    if (!this.free(x, y + 1, z) || !this.free(x, y + 2, z)) return null;
    return [x, y + 1, z];
  }

  /** A free surface spot within `r` of (cx, cz) and at least 1.5 blocks from it. */
  private floorNear(cx: number, cz: number, r: number): [number, number, number] | null {
    const rnd = this.d.host.rnd;
    for (let i = 0; i < 12; i++) {
      const s = this.surface(Math.floor(cx + (rnd() * 2 - 1) * r), Math.floor(cz + (rnd() * 2 - 1) * r));
      if (s && (s[0] + 0.5 - cx) ** 2 + (s[2] + 0.5 - cz) ** 2 >= 2.25) return [s[0] + 0.5, s[1], s[2] + 0.5];
    }
    return null;
  }

  /** WanderingTraderSpawner: a free surface spot 16-48 blocks from the player (around the village meeting point when one is near). Never a second trader. */
  trySpawn(): boolean {
    const d = this.d, p = d.host.player, rnd = d.host.rnd;
    if (!p.alive || this.hasTrader()) return false;
    let cx = p.x, cz = p.z, r0 = 24, r1 = 48;
    let anchor: [number, number] | null = null;
    let bd = 48 * 48;
    for (const m of d.entities.mobs) {
      const meet = m.type === 'villager' ? (m as unknown as { meet: [number, number, number] | null }).meet : null;
      if (!meet) continue;
      const dd = (meet[0] - p.x) ** 2 + (meet[2] - p.z) ** 2;
      if (dd < bd) { bd = dd; anchor = [meet[0] + 0.5, meet[2] + 0.5]; }
    }
    if (anchor) { cx = anchor[0]; cz = anchor[1]; r0 = 4; r1 = 12; }
    for (let i = 0; i < 24; i++) {
      const a = rnd() * Math.PI * 2, r = r0 + rnd() * (r1 - r0);
      const x = Math.floor(cx + Math.cos(a) * r), z = Math.floor(cz + Math.sin(a) * r);
      if ((x + 0.5 - p.x) ** 2 + (z + 0.5 - p.z) ** 2 < 16 * 16) continue;
      const s = this.surface(x, z);
      if (!s) continue;
      const t = this.spawnTrader(s[0] + 0.5, s[1], s[2] + 0.5, anchor);
      if (t) return true;
    }
    return false;
  }

  // ------------------------------------------------------------ interaction

  /** Right-click on `mob` holding `held`. True = handled (swing the arm). */
  interact(mob: Mob, held: Stack, o: { ctrl?: boolean; sneaking?: boolean } = {}): boolean {
    const d = this.d;
    const id = isEmpty(held) ? 0 : held.id;
    if (id && isSpawnEgg(id)) return false;
    if (mob instanceof WanderingTrader) {
      if (!mob.alive || o.ctrl) return false;
      mob.trading = true;
      d.sound?.('entity.wandering_trader.trade', mob.x, mob.y + 1, mob.z);
      d.openTrade(mob, () => mob.onTradeClosed());
      return true;
    }
    if (mob instanceof Llama) {
      const r = mob.rightClick(id, !!o.sneaking, d.host.rnd);
      if (r === 'consumed') { if (!d.creative()) d.consumeHeld(1); return true; }
      if (r === 'open') { d.openLlama(mob, () => {}); return true; }
      return r === 'handled';
    }
    return false;
  }
}
